import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SignupConfigProvider } from './contexts/SignupConfigContext';

// Marketing Pages
import MarketingHome from './pages/marketing/Home';
import Pricing from './pages/marketing/Pricing';
import RequestAccess from './pages/marketing/RequestAccess';
import APIDocs from './pages/marketing/APIDocs';
import DeveloperPortal from './pages/marketing/DeveloperPortal';
import TermsConditions from './pages/marketing/TermsConditions';
import PrivacyPolicy from './pages/marketing/PrivacyPolicy';
import RefundPolicy from './pages/marketing/RefundPolicy';
import ContactUs from './pages/marketing/ContactUs';
import TryOnPage from './pages/marketing/TryOnPage';
import MomentsPage from './pages/marketing/MomentsPage';
import TryOnStudioPage from './pages/marketing/TryOnStudioPage';
import WhyNanokart from './pages/marketing/WhyNanokart';
import Solutions from './pages/marketing/Solutions';
import AboutUs from './pages/marketing/AboutUs';
import NotFound from './pages/marketing/NotFound';
import SharedVTO from './pages/SharedVTO';
import MiraApp from './mira/MiraApp';
import PublicTryOn from './pages/public/PublicTryOn';
import CustomerTerms from './pages/public/CustomerTerms';
import PublicTryOnStudio from './pages/public/PublicTryOnStudio';
import CustomerTryOnEntry from './pages/public/CustomerTryOnEntry';
import CustomerTryOnStudio from './pages/public/CustomerTryOnStudio';

// Partner Pages
import PartnerLogin from './pages/partners/Login';
import PartnerRegister from './pages/partners/Register';
import AuthCallback from './pages/partners/AuthCallback';
import PartnerDashboard from './pages/partners/Dashboard';
import TryOnStudio from './pages/partners/PartnerTryOnStudio';
import InStore from './pages/partners/InStore';
import Social from './pages/partners/Social';
import Gallery from './pages/partners/Gallery';
import Wallet from './pages/partners/Wallet';
import APIKeys from './pages/partners/APIKeys';
import Integrations from './pages/partners/Integrations';
import Customers from './pages/partners/Customers';
import Settings from './pages/partners/Settings';
import AuditLogs from './pages/partners/AuditLogs';
import Team from './pages/partners/Team';
import Subscription from './pages/partners/Subscription';
import HelpCenter from './pages/partners/HelpCenter';
import AnalyticsDashboard from './pages/partners/AnalyticsDashboard';
import Referral from './pages/partners/Referral';
import SmartGate from './pages/partners/SmartGate';
import SmartGateWallets from './pages/partners/SmartGateWallets';
import SmartGateWalletDetail from './pages/partners/SmartGateWalletDetail';
import PartnerLayout from './components/PartnerLayout';

// Inventory Pages
import Inventory from './pages/partners/Inventory';
import InventoryCreate from './pages/partners/InventoryCreate';
import InventoryDetail from './pages/partners/InventoryDetail';
import InventoryImages from './pages/partners/InventoryImages';
import InventoryVariants from './pages/partners/InventoryVariants';
import InventoryShareLinks from './pages/partners/InventoryShareLinks';
import InventoryBulkUpload from './pages/partners/InventoryBulkUpload';
import PublicTryOnAnalytics from './pages/partners/PublicTryOnAnalytics';

// Admin Pages
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import AdminPartners from './pages/admin/Partners';
import AdminPartnerDetail from './pages/admin/PartnerDetail';
import AdminTickets from './pages/admin/Tickets';
import AdminOperations from './pages/admin/AdminOperations';
import AdminPayments from './pages/admin/Payments';
import AdminSystemConfig from './pages/admin/SystemConfig';
import SubAdminManagement from './pages/admin/SubAdminManagement';
import AdminLayout from './components/AdminLayout';

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to={requireAdmin ? '/admin/login' : '/partners/login'} replace />;
  }
  
  if (requireAdmin && !user?.is_admin) {
    return <Navigate to="/partners" replace />;
  }
  
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <SignupConfigProvider>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-dark-950">
              <Routes>
                {/* Marketing Routes */}
                <Route
                  path="/"
                  element={
                    window.location.hostname === 'api.nanokart.ai'
                      ? <Navigate to="/developer" replace />
                      : <MarketingHome />
                  }
                />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/tryon" element={<TryOnPage />} />
              <Route path="/moments" element={<MomentsPage />} />
              <Route path="/tryon-studio" element={<TryOnStudioPage />} />
              <Route path="/why-nanokart" element={<WhyNanokart />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/request-access" element={<RequestAccess />} />
              <Route path="/docs" element={<APIDocs />} />
          <Route path="/developer" element={<DeveloperPortal />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/refunds" element={<RefundPolicy />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/share/:shareToken" element={<SharedVTO />} />
            <Route path="/t/:token" element={<PublicTryOn />} />
            <Route path="/studio/:token" element={<PublicTryOnStudio />} />
            <Route path="/customer-terms" element={<CustomerTerms />} />
            
            {/* Fixed In-Store TryOn Routes */}
            <Route path="/:partnerId/customers/tryon" element={<CustomerTryOnEntry />} />
            <Route path="/:partnerId/session/:sessionId" element={<CustomerTryOnStudio />} />
            
            {/* Partner Auth Routes */}
            <Route path="/partners/login" element={<PartnerLogin />} />
            <Route path="/partners/register" element={<PartnerRegister />} />
            <Route path="/partners/auth/callback" element={<AuthCallback />} />
            
            {/* Partner Dashboard Routes */}
            <Route path="/partners" element={
              <ProtectedRoute>
                <PartnerLayout />
              </ProtectedRoute>
            }>
              <Route index element={<PartnerDashboard />} />
              <Route path="in-store" element={<InStore />} />
              <Route path="social" element={<Social />} />
              <Route path="tryon" element={<TryOnStudio />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="api-keys" element={<APIKeys />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="customers" element={<Customers />} />
              <Route path="team" element={<Team />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              {/* Legacy routes - redirect customer-engagement to in-store */}
              <Route path="customer-engagement" element={<Navigate to="/partners/in-store" replace />} />
              <Route path="tryon-analytics" element={<Navigate to="/partners/analytics" replace />} />
              <Route path="public-tryon-analytics" element={<Navigate to="/partners/in-store" replace />} />
              <Route path="referral" element={<Referral />} />
              <Route path="help" element={<HelpCenter />} />
              <Route path="settings" element={<Settings />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              {/* SmartGate Routes */}
              <Route path="smartgate" element={<SmartGate />} />
              <Route path="smartgate/wallets" element={<SmartGateWallets />} />
              <Route path="smartgate/wallets/:customerId" element={<SmartGateWalletDetail />} />
              {/* Inventory Routes */}
              <Route path="inventory" element={<Inventory />} />
              <Route path="inventory/new" element={<InventoryCreate />} />
              <Route path="inventory/bulk-upload" element={<InventoryBulkUpload />} />
              <Route path="inventory/:productId" element={<InventoryDetail />} />
              <Route path="inventory/:productId/images" element={<InventoryImages />} />
              <Route path="inventory/:productId/variants" element={<InventoryVariants />} />
              <Route path="inventory/:productId/share-links" element={<InventoryShareLinks />} />
            </Route>
            
            {/* Admin Auth Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Admin Dashboard Routes */}
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="partners/:partnerId" element={<AdminPartnerDetail />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="operations" element={<AdminOperations />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="system-config" element={<AdminSystemConfig />} />
              <Route path="sub-admins" element={<SubAdminManagement />} />
            </Route>
            
            {/* MAHI (internal codename: MIRA) — Consumer app (isolated module under /mira/*) */}
            <Route path="/mira/*" element={<MiraApp />} />

            {/* 404 Page - Proper not found handling */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#ffffff',
                color: '#0f172a',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              },
              success: {
                iconTheme: {
                  primary: '#2563eb',
                  secondary: '#ffffff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#dc2626',
                  secondary: '#ffffff',
                },
              },
            }}
          />
        </div>
      </Router>
      </AuthProvider>
      </SignupConfigProvider>
    </HelmetProvider>
  );
}

export default App;
